import { useEffect, useRef } from 'react';

/**
 * Custom hook to detect USB barcode scanner input via keystroke timing.
 * USB scanners send characters < 50ms apart and end with Enter.
 *
 * @param {function} onScan - Callback when barcode is detected: (barcodeString) => void
 * @param {object} options - { enabled: true, minLength: 3, maxDelay: 50 }
 */
const useBarcodeScanner = (onScan, options = {}) => {
  const { enabled = true, minLength = 3, maxDelay = 50 } = options;

  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      const now = Date.now();
      const timeDiff = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      // Clear any pending timeout
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // If too much time passed, reset buffer
      if (timeDiff > maxDelay && bufferRef.current.length > 0) {
        bufferRef.current = '';
      }

      if (e.key === 'Enter') {
        // Check if buffer has enough chars for a barcode
        if (bufferRef.current.length >= minLength) {
          e.preventDefault();
          e.stopPropagation();
          onScan(bufferRef.current);
        }
        bufferRef.current = '';
        return;
      }

      // Only accept printable characters
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // If this is the first char or chars are coming fast (scanner)
        if (bufferRef.current.length === 0 || timeDiff <= maxDelay) {
          bufferRef.current += e.key;
        } else {
          // Too slow — this is manual typing, reset
          bufferRef.current = e.key;
        }
      }

      // Safety timeout: clear buffer after 200ms of inactivity
      timerRef.current = setTimeout(() => {
        bufferRef.current = '';
      }, 200);
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [enabled, onScan, minLength, maxDelay]);
};

export default useBarcodeScanner;
